// src/components/compliance/modals/TaskNextStepModal.jsx

import React from "react";
import { MetaField } from "../../remediation/SharedRemediationUI";
import { getTaskStatusLabel } from "../../../utils/complianceFormatters";
import ModalShell from "./shared/ModalShell";
import MetricTile from "./shared/MetricTile";
import ModalSection from "./shared/ModalSection";
import ModalActionRow from "./shared/ModalActionRow";

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 16,
};

const itemCardStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 16,
  padding: 16,
  background: "rgba(15, 23, 42, 0.42)",
  display: "grid",
  gap: 12,
};

export default function TaskNextStepModal({
  modal,
  remediationTaskUpdating,
  fullAssessmentRunning,
  onClose,
  onMarkReadyForValidation,
  onRunFullCloudAssessment,
  onReviewTask,
}) {
  if (!modal?.stage) return null;

  const isRotationComplete = modal.stage === "rotation_complete";
  const primaryTitle = isRotationComplete
    ? "Rotation evidence is complete."
    : "Task is ready for validation.";
  const primaryDetail = isRotationComplete
    ? "The next workflow action is to mark this task Ready for Validation. OASYS will still verify the real AWS evidence before the task can pass."
    : "Next, run Full Cloud Assessment. OASYS will sync source evidence, recalculate posture, and pass or fail the task based on the latest IAM credential report.";

  return (
    <ModalShell
      open
      ariaLabel={modal.title || "Remediation next step"}
      eyebrow="OASYS · REMEDIATION NEXT STEP"
      title={modal.title}
      subtitle={modal.detail}
      onClose={onClose}
      maxWidth="760px"
      accentBorder="rgba(74, 222, 128, 0.45)"
      zIndex={125}
    >
      <div style={metricGridStyle}>
        <MetricTile
          label="Task"
          value={modal.task?.rule_id || "—"}
          sub="rule awaiting action"
          color="#60a5fa"
        />
        <MetricTile
          label="Current Status"
          value={getTaskStatusLabel(modal.task?.status)}
          sub="workflow state"
          color="#fbbf24"
        />
        <MetricTile
          label="Validation"
          value={getTaskStatusLabel(modal.task?.validation_status || "pending")}
          sub="latest validation state"
          color="#60a5fa"
        />
      </div>

      <ModalSection
        title="Next-Step Activity"
        description="Complete the next controlled remediation action before closing the task audit trail."
        badge="action required"
        badgeTone="ok"
      >
        <article style={itemCardStyle}>
          <h4 style={{ margin: 0, color: "#f8fafc", fontSize: 18, lineHeight: 1.25 }}>
            {primaryTitle}
          </h4>
          <p style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            {primaryDetail}
          </p>
        </article>

        <article style={itemCardStyle}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
            <MetaField label="Task" value={modal.task?.rule_id || "—"} wrap />
            <MetaField label="Current Status" value={getTaskStatusLabel(modal.task?.status)} />
            <MetaField label="Validation" value={getTaskStatusLabel(modal.task?.validation_status || "pending")} />
            <MetaField label="Resource" value={modal.task?.resource_id || "—"} wrap />
          </div>

          <ModalActionRow
            actions={[
              isRotationComplete
                ? {
                    key: "mark-ready",
                    label: "Mark Ready for Validation",
                    className: "btn btn-primary btn-sm",
                    disabled: remediationTaskUpdating,
                    onClick: () => onMarkReadyForValidation(modal.task),
                  }
                : {
                    key: "run-assessment",
                    label: "Run Full Cloud Assessment",
                    className: "btn btn-primary btn-sm",
                    disabled: fullAssessmentRunning,
                    onClick: onRunFullCloudAssessment,
                  },
              {
                key: "review",
                label: "Review / Manage",
                className: "btn btn-ghost btn-sm",
                onClick: () => onReviewTask(modal.task),
              },
              {
                key: "continue",
                label: "Continue Editing",
                className: "btn btn-ghost btn-sm",
                onClick: onClose,
              },
            ]}
          />
        </article>
      </ModalSection>
    </ModalShell>
  );
}